import * as React from 'react';
import { Route, Routes, Link as RouterLink, useLocation } from 'react-router-dom';
import { Button, Spinner } from '@/components/ui/button';
import { ErrorNotice } from '@/components/ui/misc';
import { configureApi } from '@/api/http';
import { useRuntimeConfig } from '@/config/ConfigProvider';
import { AuthCallback, RequireAuth, useAuth } from '@/auth/AuthProvider';
import { CALLBACK_PATH } from '@/auth/oidc';
import { MeProvider } from '@/app/MeProvider';
import { AppLayout } from '@/app/AppLayout';
import { ErrorBoundary } from '@/app/ErrorBoundary';
import { setUnauthorizedHandler } from '@/lib/useApiData';
import { extraRoutes } from '@/pages/extra/routes';

/**
 * Pages are loaded on demand.
 *
 * Each page group is its own chunk so the first authenticated view — the dashboard —
 * does not pull in the invoice editor, the settings forms or the banking pages. The
 * public pay page is split too: a client paying an invoice should never download the
 * authenticated application's code.
 */
const DashboardPage = lazyPage(() => import('@/pages/dashboard/DashboardPage'), 'DashboardPage');
const ClientsPage = lazyPage(() => import('@/pages/clients/ClientsPage'), 'ClientsPage');
const ClientFormPage = lazyPage(() => import('@/pages/clients/ClientFormPage'), 'ClientFormPage');
const ClientDetailPage = lazyPage(
  () => import('@/pages/clients/ClientDetailPage'),
  'ClientDetailPage',
);
const InvoicesPage = lazyPage(() => import('@/pages/invoices/InvoicesPage'), 'InvoicesPage');
const InvoiceEditorPage = lazyPage(
  () => import('@/pages/invoices/InvoiceEditorPage'),
  'InvoiceEditorPage',
);
const InvoiceDetailPage = lazyPage(
  () => import('@/pages/invoices/InvoiceDetailPage'),
  'InvoiceDetailPage',
);
const PaymentsPage = lazyPage(() => import('@/pages/payments/PaymentsPage'), 'PaymentsPage');
const RecurringPage = lazyPage(() => import('@/pages/recurring/RecurringPage'), 'RecurringPage');
const RecurringFormPage = lazyPage(
  () => import('@/pages/recurring/RecurringFormPage'),
  'RecurringFormPage',
);
const RecurringDetailPage = lazyPage(
  () => import('@/pages/recurring/RecurringDetailPage'),
  'RecurringDetailPage',
);
const SettingsPage = lazyPage(() => import('@/pages/settings/SettingsPage'), 'SettingsPage');
const PublicInvoicePage = lazyPage(
  () => import('@/pages/public/PublicInvoicePage'),
  'PublicInvoicePage',
);

/** `React.lazy` for a named export, so pages do not each need a `default`. */
function lazyPage(
  loader: () => Promise<Record<string, unknown>>,
  name: string,
) {
  return React.lazy(async () => ({
    default: (await loader())[name] as React.ComponentType,
  }));
}

/**
 * The application shell.
 *
 * `/pay/:token` is public: no authentication, no app chrome. The OAuth callback is
 * outside the guard because it *is* how a session is established. Everything else is
 * behind {@link RequireAuth} inside the {@link AppLayout}, under a {@link MeProvider}
 * so the environment badge and feature flags are available to every page.
 */
export function App() {
  const config = useRuntimeConfig();
  const auth = useAuth();

  // The API client is a module so non-React callers can use it too; this is where it
  // is given its base URL and token source, once config and auth both exist. A 401
  // from any data hook restarts the shell in oidc mode (which then redirects to
  // sign-in); in local mode a 401 is a genuine error and is shown, not swallowed.
  React.useEffect(() => {
    configureApi({ baseUrl: config.apiBaseUrl, getSessionValue: auth.getToken });
  }, [config.apiBaseUrl, auth.getToken]);

  React.useEffect(() => {
    setUnauthorizedHandler(() => {
      if (config.auth.mode === 'oidc' && auth.status === 'authenticated') {
        window.location.reload();
      }
    });
    return () => setUnauthorizedHandler(undefined);
  }, [config.auth.mode, auth.status]);

  return (
    <Routes>
      {/* Public, standalone, anonymous. Rendered with its own error boundary so a
          failure on a client-facing page never shows the authenticated shell. */}
      <Route
        path="/pay/:token"
        element={
          <ErrorBoundary>
            <React.Suspense fallback={<FullScreenLoading label="Loading invoice…" />}>
              <PublicInvoicePage />
            </React.Suspense>
          </ErrorBoundary>
        }
      />

      {/* The callback is outside the guard: it is how a session is established, so
          requiring one to reach it would be circular. */}
      <Route path={CALLBACK_PATH} element={<AuthCallback />} />

      {/* Everything else is authenticated and framed by the app layout. */}
      <Route
        path="/*"
        element={
          <RequireAuth>
            <MeProvider>
              <AppLayout>
                <ErrorBoundary>
                  <React.Suspense fallback={<FullScreenLoading label="Loading…" />}>
                    <AuthedRoutes />
                  </React.Suspense>
                </ErrorBoundary>
              </AppLayout>
            </MeProvider>
          </RequireAuth>
        }
      />
    </Routes>
  );
}

function AuthedRoutes() {
  return (
    <Routes>
      <Route path="/" element={<DashboardPage />} />

      <Route path="/clients" element={<ClientsPage />} />
      <Route path="/clients/new" element={<ClientFormPage />} />
      <Route path="/clients/:id" element={<ClientDetailPage />} />
      <Route path="/clients/:id/edit" element={<ClientFormPage />} />

      <Route path="/invoices" element={<InvoicesPage />} />
      <Route path="/invoices/new" element={<InvoiceEditorPage />} />
      <Route path="/invoices/:id" element={<InvoiceDetailPage />} />
      <Route path="/invoices/:id/edit" element={<InvoiceEditorPage />} />

      <Route path="/payments" element={<PaymentsPage />} />

      <Route path="/recurring" element={<RecurringPage />} />
      <Route path="/recurring/new" element={<RecurringFormPage />} />
      <Route path="/recurring/:id" element={<RecurringDetailPage />} />
      <Route path="/recurring/:id/edit" element={<RecurringFormPage />} />

      {/* One page per Settings section; bare /settings opens the first one. */}
      <Route path="/settings" element={<SettingsPage />} />
      <Route path="/settings/:section" element={<SettingsPage />} />

      {/* Banking / Books / Cloud pages, contributed by the sibling worker. Each is a
          data-router RouteObject, rendered here as an element route by path. */}
      {extraRoutes.map((route, i) => (
        <Route key={route.path ?? `extra-${i}`} path={route.path} element={route.element} />
      ))}

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function FullScreenLoading({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center" role="status">
      <span className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner className="size-4" />
        {label}
      </span>
    </div>
  );
}

function NotFound() {
  const location = useLocation();
  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="w-full max-w-md space-y-3">
        <ErrorNotice
          title="Page not found"
          message={`Nothing matches ${location.pathname} in this application.`}
        />
        <Button asChild>
          <RouterLink to="/">Back to overview</RouterLink>
        </Button>
      </div>
    </div>
  );
}
